import { useState } from 'react';
import { IconLoader2, IconX } from '@tabler/icons-react';
import toast from 'react-hot-toast';
import { useOrganizations } from '../../../hooks/useOrganizations';
import { useAssignTenantAccess } from '../../../hooks/useTenants';
import MultiSelect from '../../../components/common/MultiSelect';

const FIXED_SCOPES = ['LVE', 'TS', 'HR'];

export default function AssignAccessModal({ isOpen, onClose, employee }) {
    const [selectedOrgIds, setSelectedOrgIds] = useState([]);

    const { data: orgsData, isLoading: loadingOrgs } = useOrganizations();
    const organizations = orgsData?.results || orgsData || [];

    const assignAccess = useAssignTenantAccess();

    const handleClose = () => {
        setSelectedOrgIds([]);
        onClose();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (selectedOrgIds.length === 0) {
            toast.error('Please select at least one organization');
            return;
        }

        try {
            await assignAccess.mutateAsync({
                userId: employee.user,
                orgIds: selectedOrgIds,
                scopes: FIXED_SCOPES,
            });
            toast.success(`Access assigned to ${employee.name} successfully`);
            handleClose();
        } catch (error) {
            const msg =
                error?.response?.data?.detail ||
                error?.response?.data?.message ||
                (error?.response?.data && Object.values(error.response.data)[0]?.[0]) ||
                error.message ||
                'Failed to assign access';
            toast.error(msg);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="bg-bg-dark rounded-xl shadow-xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200 border border-border">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-bg-light">
                    <div>
                        <h3 className="text-lg font-bold text-text-primary">Assign Access</h3>
                        <p className="text-sm text-text-secondary">Grant {employee?.name} cross-organization access</p>
                    </div>
                    <button onClick={handleClose} className="p-2 hover:bg-border/50 rounded-full transition-colors">
                        <IconX size={20} className="text-text-secondary" />
                    </button>
                </div>

                {/* Body */}
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {/* Organizations */}
                    <div className="mb-40">
                        <label className="block text-sm font-medium text-text-primary mb-2">
                            Organizations <span className="text-danger">*</span>
                        </label>
                        <MultiSelect
                            options={organizations}
                            selectedIds={selectedOrgIds}
                            onChange={setSelectedOrgIds}
                            placeholder={loadingOrgs ? 'Loading organizations...' : 'Search and select organizations...'}
                            disabled={loadingOrgs}
                            displayField="name"
                            valueField="id"
                        />
                    </div>

                    {/* Footer */}
                    <div className="flex justify-end gap-3 pt-6 border-t border-border mt-2">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="px-4 py-2 text-sm font-medium text-text-primary bg-bg-light border border-border rounded-lg hover:bg-border/50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={assignAccess.isPending || selectedOrgIds.length === 0}
                            className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {assignAccess.isPending && <IconLoader2 size={16} className="animate-spin" />}
                            Assign Access
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
